import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Box, Button, IconButton, Stack, TextField, Typography} from 'views/components';
import {AttachFileOutlined, Close} from 'views/icons';

import UploadFileToS3 from '../secureStorage/UploadFileToS3';

import backend from 'utils/backend';

const MessageComposer = (props) => {
  const {
    conversationRn,
    onSend,
    placeholder,
    sendLabel,
    sendFailedLabel,
    user,
  } = props;

  const [message, setMessage] = React.useState('');
  const [pendingFiles, setPendingFiles] = React.useState([]);
  const [isSending, setIsSending] = React.useState(false);
  const [sendFailed, setSendFailed] = React.useState(false);

  React.useEffect(() => {
    setMessage('');
    setPendingFiles([]);
    setSendFailed(false);
  }, [conversationRn]);

  const isSubmitDisabled = isSending || (!message.trim() && pendingFiles.length === 0);

  const onSubmit = async () => {
    if (isSubmitDisabled) return;
    setIsSending(true);
    setSendFailed(false);
    try {
      await onSend({conversationRn, requesterRn: user.userRn, message, files: pendingFiles});
      setMessage('');
      setPendingFiles([]);
    } catch (error) {
      console.error('Failed to send the chat message:', error);
      setSendFailed(true);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    onSubmit();
  };

  const getSignedUrl = (file) => {
    return new Promise((resolve, reject) => {
      (async () => {
        try {
          const result = await backend.fileStorage.uploadFile({
            fileDir: `channel/${conversationRn}`,
            fileName: file.name,
            fileType: file.type,
          });

          resolve({
            signedUrl: result.signedUrl,
            fileUrl: result.fileUrl,
            onUploadComplete: (fileUrl) => {
              setPendingFiles((currentFiles) => currentFiles.concat({
                name: file.name,
                type: file.type,
                src: fileUrl,
              }));
            },
          });
        } catch (error) {
          console.error('Failed to get a signed upload url:', error);
          reject(error);
        }
      })();
    });
  };

  const removePendingFile = (src) => {
    setPendingFiles((currentFiles) => currentFiles.filter((file) => file.src !== src));
  };

  return (
    <Box sx={{p: '14px 20px', borderTop: '1px solid #ECECF1', backgroundColor: '#FFFFFF'}}>
      {
        pendingFiles.length > 0 && (
          <Stack direction='row' sx={{gap: 1, flexWrap: 'wrap', mb: 1}}>
            {
              pendingFiles.map((file) => (
                <Stack
                  key={file.src}
                  direction='row'
                  sx={{
                    alignItems: 'center',
                    gap: 0.5,
                    border: '1px solid #ECECF1',
                    borderRadius: '999px',
                    backgroundColor: '#F7F8FA',
                    pl: '12px',
                    pr: '4px',
                    py: '4px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '12px',
                      fontWeight: 500,
                      color: '#050A14',
                      maxWidth: '160px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {file.name}
                  </Typography>
                  <IconButton onClick={() => removePendingFile(file.src)} size='small'>
                    <Close sx={{fontSize: 14, color: '#9A9BAE'}} />
                  </IconButton>
                </Stack>
              ))
            }
          </Stack>
        )
      }
      <Stack direction='row' sx={{gap: 1, alignItems: 'center'}}>
        <UploadFileToS3 getSignedUrl={getSignedUrl}>
          <IconButton
            disabled={isSending}
            sx={{
              'height': '42px',
              'width': '42px',
              'flexShrink': 0,
              'border': '1px solid #DCDDE5',
              'borderRadius': '9px',
              'color': '#63708A',
              '&:hover': {
                backgroundColor: '#F7F8FA',
                borderColor: '#D1D3DD',
              },
            }}
          >
            <AttachFileOutlined sx={{fontSize: 18}} />
          </IconButton>
        </UploadFileToS3>
        <TextField
          fullWidth
          disabled={isSending}
          placeholder={placeholder}
          value={message}
          onChange={setMessage}
          onKeyDown={handleKeyDown}
          autoComplete='off'
          sx={{
            'width': '100%',
            '& .MuiOutlinedInput-root': {
              'backgroundColor': 'common.white',
              'borderRadius': '9px',
              'height': '42px',
              '& fieldset': {
                'borderStyle': 'solid',
                'borderColor': '#DCDDE5',
              },
              '&:hover fieldset': {
                'borderColor': '#D1D3DD',
              },
            },
            '& .MuiOutlinedInput-input': {
              'px': '16px',
              'py': '10px',
              'fontSize': '13px',
              'lineHeight': '150%',
              'whiteSpace': 'pre',
              'color': '#050A14',
              'caretColor': '#050A14',
            },
            '& .MuiOutlinedInput-input::placeholder': {
              'color': '#7F8493',
              'opacity': 1,
            },
          }}
        />
        <Button
          disabled={isSubmitDisabled}
          onClick={onSubmit}
          sx={{
            'height': '42px',
            'px': '16px',
            'flexShrink': 0,
            'borderRadius': '9px',
            'backgroundColor': '#EB7A58',
            'color': '#FFFFFF',
            'textTransform': 'none',
            'boxShadow': 'none',
            '&:hover': {
              backgroundColor: '#D96845',
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              backgroundColor: '#F0F0F3',
              color: '#B4B6C2',
            },
          }}
        >
          {sendLabel}
        </Button>
      </Stack>
      {
        sendFailed &&
          <Typography sx={{fontSize: '12px', fontWeight: 400, color: 'error.main', mt: 0.75}}>
            {sendFailedLabel}
          </Typography>
      }
    </Box>
  );
};

MessageComposer.propTypes = {
  conversationRn: PropTypes.string.isRequired,
  onSend: PropTypes.func.isRequired,
  placeholder: PropTypes.string.isRequired,
  sendLabel: PropTypes.string.isRequired,
  sendFailedLabel: PropTypes.string.isRequired,
  user: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({user: state.user});

export default connect(mapStateToProps, dispatch)(React.memo(MessageComposer));
